import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";

export class ShaperCpu extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Shaper CPU Utilization";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper CPU Utilization</h5><p>Average and peak CPU usage over time.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.lastGraphState !== null;
    }

    explain() {
        if (!this.lastGraphState || !this.lastGraphState.body) return null;
        const ticks = this.lastGraphState.body.usage_ticks || [];
        const avgRaw = this.lastGraphState.body.average_cpu_usage || [];
        const avg = normalizePercentSeries(avgRaw);
        if (avg.length === 0) return null;

        const maxAvg = maxFinite(avg);
        if (maxAvg === null) return null;

        const sustainedHigh = sustainedRunInfo(avg, 85, 3, ticks);
        const sustainedVeryHigh = sustainedRunInfo(avg, 95, 3, ticks);

        let level = null;
        let runInfo = null;
        if (sustainedVeryHigh && sustainedVeryHigh.runLength >= 3) {
            level = "very high";
            runInfo = sustainedVeryHigh;
        } else if (sustainedHigh && sustainedHigh.runLength >= 3) {
            level = "high";
            runInfo = sustainedHigh;
        } else {
            return null;
        }

        let description = `CPU was ${level} for a sustained stretch`;
        if (runInfo && runInfo.durationText) {
            description += ` (${runInfo.durationText})`;
        }
        description += `. Average reached about ${maxAvg.toFixed(0)}%.`;
        return { dashlet_name: this.title(), description };
    }

    buildContainer() {
        let base = super.buildContainer();
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCpuGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return ["ShaperDetail"]; 
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [_key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value);
            this.lastGraphState = value;
        }
        this.rendered = true;
    }
}

function normalizePercentSeries(values) {
    const list = Array.isArray(values) ? values : [];
    return list.map(v => (Number.isFinite(v) ? v : null));
}

function normalizePeakSeries(peaksRaw, avgSeries) {
    const list = Array.isArray(peaksRaw) ? peaksRaw : [];
    return list.map((v, idx) => normalizePeakValue(v, avgSeries ? avgSeries[idx] : null));
}

function normalizePeakValue(peak, avg) {
    if (!Number.isFinite(peak)) return null;
    if (peak <= 100) return peak;
    if (!Number.isFinite(avg) || avg <= 0) return Math.min(peak, 100);
    const estCores = Math.max(1, Math.round(peak / avg));
    return peak / estCores;
}

function maxFinite(values) {
    let max = null;
    values.forEach(v => {
        if (!Number.isFinite(v)) return;
        if (max === null || v > max) max = v;
    });
    return max;
}

function sustainedRunInfo(values, threshold, minSamples, ticks) {
    if (!Array.isArray(values)) return null;
    let maxRun = 0;
    let cur = 0;
    let endIdx = -1;
    for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (Number.isFinite(v) && v >= threshold) {
            cur += 1;
            if (cur > maxRun) {
                maxRun = cur;
                endIdx = i;
            }
        } else {
            cur = 0;
        }
    }
    if (maxRun < minSamples) {
        return { runLength: maxRun, durationText: null };
    }
    let durationText = null;
    if (Array.isArray(ticks) && endIdx >= 0 && maxRun > 1 && ticks.length > endIdx) {
        const startIdx = endIdx - maxRun + 1;
        const delta = ticks[endIdx] - ticks[startIdx];
        if (Number.isFinite(delta) && delta > 0) {
            durationText = formatDuration(delta);
        }
    }
    return { runLength: maxRun, durationText };
}

function formatDuration(seconds) {
    if (!Number.isFinite(seconds)) return null;
    if (seconds >= 3600) {
        const hours = Math.max(1, Math.round(seconds / 3600));
        return `about ${hours} hour${hours === 1 ? "" : "s"}`;
    }
    const minutes = Math.max(1, Math.round(seconds / 60));
    return `about ${minutes} minute${minutes === 1 ? "" : "s"}`;
}

class ShaperCpuGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxisPercent("% Utilization", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();

        this.option.series = [];
        this.option.xAxis.data = [];
        this.option.legend.data = [];

        const ticks = data.body.usage_ticks || [];
        // Normalize peak values if the source reports total CPU across cores.
        const avgRaw = data.body.average_cpu_usage || [];
        const peakRaw = data.body.peak_cpu_usage || [];
        const avg = avgRaw.map(v => (v == null ? v : v));
        const peak = peakRaw.map((v, idx) => normalizePeakValue(v, avg[idx]));

        // X axis
        for (let i = 0; i < ticks.length; i++) {
            this.option.xAxis.data.push(ticks[i]);
        }

        // Series
        let n = 0;
        const avgSeries = {
            type: 'line',
            data: avg,
            name: 'Avg CPU',
            smooth: true,
            itemStyle: { color: window.graphPalette[n] },
        };
        n++;
        const peakSeries = {
            type: 'line',
            data: peak,
            name: 'Peak CPU (est)',
            smooth: true,
            itemStyle: { color: window.graphPalette[n] },
        };

        this.option.series.push(avgSeries);
        this.option.series.push(peakSeries);
        this.option.legend.data.push('Avg CPU');
        this.option.legend.data.push('Peak CPU (est)');

        // Tooltip
        const thatData = data;
        this.option.tooltip = {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: 'rgba(30,30,30,0.92)',
                    color: '#fff',
                    fontFamily: 'inherit',
                    formatter: function(params) {
                        if (params.axisDimension === 'x') {
                            return new Date(parseInt(params.value, 10) * 1000).toLocaleString();
                        }
                        return params.value + '%';
                    }
                }
            },
            backgroundColor: 'rgba(30,30,30,0.92)',
            textStyle: {
                fontSize: 10,
                color: '#fff',
                fontFamily: 'inherit'
            },
            formatter: function(params) {
                let result = '<div style="font-size: 10px;">';
                if (params && params.length > 0) {
                    const ts = params[0].axisValue;
                    result += new Date(parseInt(ts, 10) * 1000).toLocaleString() + '<br/>';
                }
                const idx = params[0].dataIndex;
                const avgValRaw = (thatData.body.average_cpu_usage || [])[idx];
                const peakValRaw = (thatData.body.peak_cpu_usage || [])[idx];
                const avgVal = (avgValRaw == null) ? undefined : avgValRaw;
                const peakVal = (peakValRaw == null) ? undefined : normalizePeakValue(peakValRaw, avgValRaw);
                if (avgVal !== undefined) {
                    result += '<strong>Avg:</strong> ' + avgVal.toFixed(1) + '%<br/>';
                }
                if (peakVal !== undefined) {
                    result += '<strong>Peak (est):</strong> ' + peakVal.toFixed(1) + '%';
                }
                result += '</div>';
                return result;
            }
        };

        // Axis pointer link
        this.option.axisPointer = {
            link: { xAxisIndex: 'all' },
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };

        this.chart.setOption(this.option);
    }
}
